import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { Task, TaskSubmission } from '../../types/task';
import TaskDetails from '../../components/tasks/TaskDetails';
import BasePageLayout from '../../components/BasePageLayout';
import CustomDropdown, { DropdownOption } from '../../components/ui/CustomDropdown';
import { useAuth } from '../../context/AuthContext';
import { useDeviceType, DeviceType } from '../../hooks/useDeviceType';
import { ContentLanguageMetadataService } from '../../services/contentLanguageMetadataService';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/shared/dashboard.css';
import '../../styles/pages/TasksPage.css';
import '../../styles/pages/TasksHubModern.css';
import '../../styles/pages/CompactTasksHeader.css';
import '../../styles/components/TaskCardWrapper.css';
import './PublisherPage.css';

// Helper function to extract relative path from screenshot URL
const getRelativePath = (urlOrPath: string): string => {
  if (!urlOrPath) return ''; // Handle null or undefined input
  if (urlOrPath.startsWith('http')) {
    try {
      const url = new URL(urlOrPath);
      const parts = url.pathname.split('/screenshots/');
      if (parts.length > 1) {
        const relative = parts.pop();
        return relative || ''; // Ensure not undefined
      }
    } catch (e) {
      console.error("Error parsing URL, assuming it's a path:", urlOrPath, e);
      // If URL parsing fails, it might be a malformed URL or already a path segment
      // Attempt to extract the last part if it looks like a path with bucket name
      const bucketAndPath = urlOrPath.split('/screenshots/');
      if (bucketAndPath.length > 1) {
        return bucketAndPath.pop() || '';
      }
      return urlOrPath; // Fallback
    }
  }
  return urlOrPath; // Assume it's already a relative path
};

type PublisherTab = 'open' | 'pending-review' | 'completed';

const PublisherPage: React.FC = () => {
  const [rawMyTasks, setRawMyTasks] = useState<Task[]>([]);
  const [myTaskSubmissions, setMyTaskSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'points' | 'category'>('newest');
  const [categories, setCategories] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<PublisherTab>('open');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  // Task detail panel state
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  // Responsive state
  const [showMobileView, setShowMobileView] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(10);

  const [stats, setStats] = useState({
    open: 0,
    pendingReview: 0,
    completed: 0
  });

  // Task detail states
  const [submissions, setSubmissions] = useState<TaskSubmission[]>([]);
  const [userSubmission, setUserSubmission] = useState<TaskSubmission | null>(null);
  const [screenshotDisplayUrl, setScreenshotDisplayUrl] = useState<string | null>(null);
  const [submissionScreenshotUrls, setSubmissionScreenshotUrls] = useState<Record<string, string | null>>({});
  const [verificationSubmitted, setVerificationSubmitted] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [processingReview, setProcessingReview] = useState(false);

  const { user } = useAuth();
  const { deviceType } = useDeviceType();
  const { t } = useLanguage();

  // Helper function to translate category names
  const translateCategory = useCallback((category: string): string => {
    const categoryMap: Record<string, string> = {
      'Mobile App': t('category.mobileApp'),
      'Website': t('category.website'),
      'Desktop App': t('category.desktopApp'),
      'Game': t('category.game'),
      'SaaS': t('category.saas'),
      'Other': t('category.other')
    };
    return categoryMap[category] || category;
  }, [t]);

  // Dropdown options
  const categoryOptions: DropdownOption[] = useMemo(() => [
    { value: 'all', label: t('tasks.allCategories') },
    ...categories.map(category => ({ value: category, label: translateCategory(category) }))
  ], [categories, t, translateCategory]);

  const sortOptions: DropdownOption[] = useMemo(() => [
    { value: 'newest', label: t('tasks.newestFirst') },
    { value: 'points', label: t('tasks.highestPoints') },
    { value: 'category', label: t('tasks.category') }
  ], [t]);

  // Set responsive states based on device type
  useEffect(() => {
    if (!deviceType) return;

    const isMobile = deviceType === DeviceType.MOBILE;
    const isTablet = deviceType === DeviceType.TABLET;

    setIsCompactView(isMobile || isTablet);
    setShowMobileView(isMobile || isTablet);
  }, [deviceType]);

  // Scroll effect for compact header
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.compact-tasks-header');
      if (header) {
        if (window.scrollY > 10) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Call once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Publisher Type Buttons Configuration
  const publisherTypeButtons = useMemo(() => [
    {
      id: 'open' as PublisherTab,
      label: t('tasks.open'),
      hint: t('tasks.tasksStillOpenForSubmissions'),
      icon: 'bi-unlock',
      count: stats.open,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    {
      id: 'pending-review' as PublisherTab,
      label: t('tasks.pendingReview'),
      hint: t('tasks.tasksWithSubmissionsAwaitingReview'),
      icon: 'bi-clock-history',
      count: stats.pendingReview,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    },
    {
      id: 'completed' as PublisherTab,
      label: t('tasks.completed'),
      hint: t('tasks.tasksThatHaveBeenCompleted'),
      icon: 'bi-check-circle',
      count: stats.completed,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    }
  ], [stats.open, stats.pendingReview, stats.completed, t]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to refetch and update task data (based on TasksPage logic)
  const refetchTaskData = useCallback(async () => {
    if (!user) return;

    try {
      // Execute all Supabase calls in parallel for better performance
      const [
        myTasksResult,
        myTaskSubmissionsResult
      ] = await Promise.all([
        // Fetch user's created tasks with publisher info
        supabase
          .from('tasks')
          .select(`
            *,
            publisher:users!created_by (
              id,
              username,
              avatar_url
            )
          `)
          .eq('created_by', user.id),

        // Fetch submissions for user's tasks (as creator)
        // We fetch all submissions where the user might be the task creator
        // and filter them after we know which tasks belong to this user
        supabase
          .from('task_submissions')
          .select('task_id, status, id')
      ]);

      // Check for errors from parallel calls
      if (myTasksResult.error) throw myTasksResult.error;
      if (myTaskSubmissionsResult.error) throw myTaskSubmissionsResult.error;

      // Extract data from results
      const myTasksData = myTasksResult.data;
      const allSubmissionsData = myTaskSubmissionsResult.data;

      console.log('📋 Publisher tasks data:', myTasksData);

      // Filter submissions to only include those for the user's tasks
      const taskIds = new Set((myTasksData || []).map(task => task.id));
      const myTaskSubmissions = (allSubmissionsData || []).filter(sub =>
        taskIds.has(sub.task_id)
      );

      // Calculate stats
      const toReviewCount = myTaskSubmissions.filter(sub => sub.status === 'pending').length;
      const completedTasksCount = (myTasksData || []).filter(task => task.status === 'completed').length;
      const openTasksCount = (myTasksData || []).filter(task => task.status === 'open').length;

      // Update state
      setRawMyTasks(myTasksData as Task[]);
      setMyTaskSubmissions(myTaskSubmissions);

      // Calculate stats
      setStats({
        open: openTasksCount,
        pendingReview: toReviewCount,
        completed: completedTasksCount
      });

      // Extract categories from all task data
      const categorySet = new Set<string>();
      (myTasksData || []).forEach(task => {
        if (task.category) categorySet.add(task.category);
      });
      setCategories(Array.from(categorySet));

      console.log('📊 Publisher stats:', {
        open: openTasksCount,
        pendingReview: toReviewCount,
        completed: completedTasksCount
      });

    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again later.');
    }
  }, [user]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchTasks = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Use the refetchTaskData function for initial load
        await refetchTaskData();

      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to load tasks. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch on user changes
    fetchTasks();
  }, [user?.id, refetchTaskData]);

  // Sort function
  const sortTasks = useCallback((tasks: Task[]) => {
    const sortedTasks = [...tasks];

    switch (sortBy) {
      case 'newest':
        return sortedTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'points':
        return sortedTasks.sort((a, b) => b.points - a.points);
      case 'category':
        return sortedTasks.sort((a, b) => a.category.localeCompare(b.category));
      default:
        return sortedTasks;
    }
  }, [sortBy]);

  // Filter tasks based on search and filters
  const filteredMyTasks = useMemo(() => {
    const filtered = rawMyTasks.filter(task => {
      const matchesSearch = !debouncedSearchTerm ||
        task.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });

    // Always include the selected task even if it doesn't match filters
    if (selectedTask && !filtered.find(task => task.id === selectedTask.id)) {
      const selectedTaskInRaw = rawMyTasks.find(task => task.id === selectedTask.id);
      if (selectedTaskInRaw) {
        filtered.push(selectedTaskInRaw);
      }
    }

    return sortTasks(filtered);
  }, [rawMyTasks, debouncedSearchTerm, categoryFilter, selectedTask, sortTasks]);

  // Get current tasks based on active tab
  const getCurrentTasks = useMemo(() => {
    const submissionsByTask = myTaskSubmissions.reduce((acc: Record<string, any[]>, sub) => {
      if (!acc[sub.task_id]) acc[sub.task_id] = [];
      acc[sub.task_id].push(sub);
      return acc;
    }, {});

    let filtered = filteredMyTasks;

    // Apply tab filter
    if (activeTab === 'open') {
      filtered = filtered.filter(task => task.status === 'open');
    } else if (activeTab === 'pending-review') {
      filtered = filtered.filter(task => {
        const submissions = submissionsByTask[task.id] || [];
        return submissions.some(sub => sub.status === 'pending');
      });
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(task => task.status === 'completed');
    }

    return filtered;
  }, [activeTab, filteredMyTasks, myTaskSubmissions]);

  // Pagination
  const totalPages = Math.ceil(getCurrentTasks.length / tasksPerPage);
  const currentTasks = getCurrentTasks.slice(
    (currentPage - 1) * tasksPerPage,
    currentPage * tasksPerPage
  );

  const handleTabSwitch = useCallback((tab: PublisherTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedTask(null);
    setShowTaskDetail(false);
    setVerificationSubmitted(false); // Reset verification state when switching tabs
  }, []);

  const handleTaskSelect = useCallback((task: Task) => {
    // Open the task detail panel and select the task
    setSelectedTask(prevSelected => prevSelected?.id === task.id ? prevSelected : task);
    setShowTaskDetail(true);
    setVerificationSubmitted(false); // Reset verification state when selecting a new task
  }, []);

  // Helper function to get language display name
  const getLanguageDisplay = (lang: string | null): string => {
    if (!lang) return t('tasks.english'); // Default to English
    return lang === 'zh' ? t('tasks.chinese') : t('tasks.english');
  };

  // Preserve selected task after data refresh by finding it in the updated lists
  useEffect(() => {
    if (selectedTask) {
      // Find the selected task in the updated data
      const updatedTask = rawMyTasks.find(
        task => task.id === selectedTask.id
      );

      // Update selectedTask with fresh data if found, otherwise keep the current one
      if (updatedTask && JSON.stringify(updatedTask) !== JSON.stringify(selectedTask)) {
        setSelectedTask(updatedTask);
      }
    }
  }, [rawMyTasks, selectedTask]);

  // Fetch task submissions when a task is selected (from TasksPage logic)
  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!selectedTask || !user) return;

      try {
        setSubmissionsLoading(true);

        // Prepare requests array for parallel execution
        const requests = [];

        // Conditionally add creator submissions request
        if (selectedTask.created_by === user.id) {
          requests.push(
            supabase
              .from('task_submissions')
              .select('*')
              .eq('task_id', selectedTask.id)
          );
        } else {
          // Add placeholder promise if user is not the creator
          requests.push(Promise.resolve({ data: null, error: null }));
        }

        // Always add user submission request
        requests.push(
          supabase
            .from('task_submissions')
            .select('*')
            .eq('task_id', selectedTask.id)
            .eq('user_id', user.id)
            .maybeSingle()
        );

        // Execute both requests in parallel
        const [allSubmissionsResult, userSubmissionResult] = await Promise.all(requests);

        // Handle errors
        if (allSubmissionsResult.error) throw allSubmissionsResult.error;
        if (userSubmissionResult.error) throw userSubmissionResult.error;

        // Update state
        if (selectedTask.created_by === user.id && allSubmissionsResult.data) {
          setSubmissions(allSubmissionsResult.data as TaskSubmission[]);
        }
        setUserSubmission(userSubmissionResult.data as TaskSubmission);

      } catch (err) {
        console.error('Error fetching submissions:', err);
      } finally {
        setSubmissionsLoading(false);
      }
    };

    if (selectedTask) {
      fetchSubmissions();
    }
  }, [selectedTask, user, verificationSubmitted]);

  // Handle user submission screenshot display (from TasksPage logic)
  useEffect(() => {
    if (user && userSubmission?.proof?.screenshot_url && userSubmission.verification_type === 'screenshot') {
      const getScreenshotUrl = async () => {
        try {
          const screenshotUrl = userSubmission.proof?.screenshot_url;
          if (!screenshotUrl) return;

          const { data, error } = await supabase.storage
            .from('screenshots')
            .createSignedUrl(screenshotUrl, 3600); // 1 hour expiry

          if (error) {
            console.error('Error creating signed URL for user submission:', error);
            return;
          }

          setScreenshotDisplayUrl(data.signedUrl);
        } catch (error) {
          console.error('Error in getScreenshotUrl:', error);
        }
      };

      getScreenshotUrl();
    } else {
      setScreenshotDisplayUrl(null);
    }
  }, [user, userSubmission]);

  // Generate signed URLs for submission screenshots  
  useEffect(() => {
    const newUrls: Record<string, string | null> = {}; // Initialize an empty object for current batch
    if (submissions && submissions.length > 0) {
      const generateAllUrls = async () => {
        for (const sub of submissions) {
          if (sub.verification_type === 'screenshot' && sub.proof?.screenshot_url) {
            const path = getRelativePath(sub.proof.screenshot_url);
            if (!path) {
              console.error(`Could not determine a valid path for submission ${sub.id} screenshot:`, sub.proof.screenshot_url);
              newUrls[sub.id] = null;
              continue; // Skip to next submission
            }
            try {
              const { data, error: signedUrlGenError } = await supabase.storage
                .from('screenshots')
                .createSignedUrl(path, 300); // 300 seconds = 5 minutes

              if (signedUrlGenError) {
                console.error(`Error generating signed URL for submission ${sub.id} (path: ${path}):`, signedUrlGenError);
                newUrls[sub.id] = null;
              } else if (data && data.signedUrl) {
                newUrls[sub.id] = data.signedUrl;
              } else {
                console.warn(`No signed URL or data returned for submission ${sub.id} (path: ${path})`, data);
                newUrls[sub.id] = null;
              }
            } catch (e) {
              console.error(`Exception generating signed URL for submission ${sub.id} (path: ${path}):`, e);
              newUrls[sub.id] = null;
            }
          } else {
            // If not a screenshot or no path, ensure its entry is null if it's supposed to be a screenshot
            if (sub.verification_type === 'screenshot') {
              newUrls[sub.id] = null;
            }
          }
        }
        setSubmissionScreenshotUrls(newUrls); // Replace the whole map
      };
      generateAllUrls();
    } else {
      setSubmissionScreenshotUrls({}); // Clear if no submissions
    }
  }, [submissions]);

  if (loading) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="loading-container">
          <div className="loading-ring"></div>
          <p>{t('tasks.loadingTasks')}</p>
        </div>
      </BasePageLayout>
    );
  }

  if (error) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="error-container">
          <h3>{t('common.error')}</h3>
          <p>{error}</p>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="tasks-hub-container">
        {/* Compact Header */}
        <div className="compact-tasks-header">
          <div className="compact-header-content">
            {/* Title Section */}
            <div className="compact-title-section">
              <div>
                <h1 className="compact-page-title">{t('tasks.publisherDashboard')}</h1>
                <div className="compact-breadcrumb">
                  <span>{t('navigation.tasks')}</span>
                  <i className="bi bi-chevron-right compact-breadcrumb-separator"></i>
                  <span>{t('navigation.publisher')}</span>
                </div>
              </div>
              <div className="compact-stats-mini">
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{stats.open}</span>
                  <span className="stat-label-mini">{t('tasks.open')}</span>
                </div>
                <div className="stat-item-mini earnings">
                  <span className="stat-number-mini">
                    {rawMyTasks.reduce((total, task) => total + task.points, 0)}
                  </span>
                  <span className="stat-label-mini">{t('tasks.totalPoints')}</span>
                </div>
              </div>
              <Link to="/create-task" className="compact-action-button compact-tooltip" data-tooltip={t('tasks.createNewTask')}>
                <i className="bi bi-plus-circle"></i>
                {t('tasks.createTask')}
              </Link>
            </div>

            {/* Controls Section */}
            <div className="compact-controls-section">
              {/* Publisher Type Buttons */}
              <div className="compact-type-buttons">
                {publisherTypeButtons.map(button => (
                  <button
                    key={button.id}
                    className={`compact-type-button compact-tooltip ${activeTab === button.id ? 'active' : ''} ${button.count === 0 ? 'disabled' : ''}`}
                    onClick={() => button.count > 0 && handleTabSwitch(button.id)}
                    disabled={button.count === 0}
                    data-tooltip={button.hint}
                    style={{
                      background: activeTab === button.id ? button.gradient : undefined
                    }}
                  >
                    <i className={`bi ${button.icon} compact-type-button-icon`}></i>
                    <span className="compact-type-button-label">{button.label}</span>
                    <span className="compact-type-button-count">{button.count}</span>
                  </button>
                ))}
              </div>

              {/* Search and Filters */}
              <div className="compact-search-filters">
                <div className="compact-search-bar">
                  <i className="bi bi-search compact-search-icon"></i>
                  <input
                    type="text"
                    className="compact-search-input"
                    placeholder={t('tasks.searchTasks')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={categoryOptions}
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    placeholder={t('tasks.category')}
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`tasks-hub-main-container ${showTaskDetail ? 'detail-panel-open' : ''}`}>
          {/* Task List */}
          <div className="tasks-hub-task-list-column">
            <div className="tasks-hub-task-list-header">
              <span className="tasks-hub-results-count">
                {getCurrentTasks.length} {getCurrentTasks.length === 1 ? t('tasks.task') : t('navigation.tasks').toLowerCase()}
              </span>
              <div className="tasks-hub-sort-controls">
                <button
                  onClick={refetchTaskData}
                  className="tasks-hub-refresh-button"
                  title={t('tasks.refreshTaskList')}
                  disabled={loading}
                >
                  <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
                </button>
                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={sortOptions}
                    value={sortBy}
                    onChange={(value) => setSortBy(value as 'newest' | 'points' | 'category')}
                    placeholder={t('tasks.sortBy')}
                    size="small"
                  />
                </div>
              </div>
            </div>

            <div className="tasks-hub-task-list-content">
              {/* Task Cards */}
              <div className="tasks-hub-task-cards">
                {currentTasks.length === 0 ? (
                  <div className="no-tasks-message">
                    <i className="bi bi-inbox"></i>
                    <h3>{t('tasks.noTasksFound')}</h3>
                    <p>
                      {activeTab === 'open'
                        ? t('tasks.noOpenTasks')
                        : activeTab === 'pending-review'
                          ? t('tasks.noPendingSubmissions')
                          : t('tasks.noCompletedTasks')
                      }
                    </p>
                    {activeTab === 'open' && (
                      <Link to="/create-task" className="btn btn-primary">
                        <i className="bi bi-plus-circle"></i>
                        {t('tasks.createYourFirstTask')}
                      </Link>
                    )}
                  </div>
                ) : (
                  currentTasks.map(task => {
                    const taskSubmissions = myTaskSubmissions.filter(sub => sub.task_id === task.id);
                    const submittedCount = taskSubmissions.filter(sub => sub.status === 'pending').length;
                    const approvedCount = taskSubmissions.filter(sub => sub.status === 'approved').length;
                    const rejectedCount = taskSubmissions.filter(sub => sub.status === 'rejected').length;

                    return (
                      <div
                        key={task.id}
                        className={`task-card-wrapper ${selectedTask?.id === task.id ? 'selected' : ''}`}
                        onClick={() => handleTaskSelect(task)}
                      >
                        <div className="task-card task-card-modern publisher-task-card">
                          <div className="task-header-row">
                            <h3 className="task-title">{task.title}</h3>
                            <div className="task-status-badges">
                              <span className={`task-status-badge ${task.status}`}>
                                <i className={`bi ${task.status === 'open' ? 'bi-unlock' :
                                  task.status === 'completed' ? 'bi-check-circle-fill' :
                                    task.status === 'cancelled' ? 'bi-x-circle-fill' : 'bi-clock-history'
                                  }`}></i>
                                {task.status}
                              </span>
                              <span className="task-points">
                                <i className="bi bi-star-fill"></i>
                                {task.points}
                              </span>
                            </div>
                          </div>

                          <p className="task-description">{task.description}</p>

                          <div className="task-meta">
                            <div className="task-meta-left">
                              <span className="task-category">
                                <i className="bi bi-tag"></i>
                                {translateCategory(task.category)}
                              </span>
                              <span className="task-verification-info">
                                <i className={`bi task-verification-icon ${task.verification_type === 'screenshot' ? 'bi-camera' :
                                  task.verification_type === 'text_response' ? 'bi-chat-text' :
                                    task.verification_type === 'email_username' ? 'bi-envelope' : 'bi-check'
                                  }`}></i>
                                {task.verification_type === 'screenshot' ? t('tasks.screenshot') :
                                  task.verification_type === 'text_response' ? t('tasks.textResponse') :
                                    task.verification_type === 'email_username' ? t('tasks.emailUsername') : t('tasks.verification')}
                              </span>
                              <span className="task-participants">
                                <i className="bi bi-people"></i>
                                {task.current_participants || 0}/{task.max_participants || 'N/A'}
                              </span>
                              <span className="task-verification-info" title={t('tasks.contentLanguage')}>
                                <i className="bi bi-translate"></i>
                                {getLanguageDisplay(ContentLanguageMetadataService.getContentLanguage('task', task.id))}
                              </span>
                            </div>
                            <div className="task-meta-right">
                              <div className="submission-stats">
                                {submittedCount > 0 && (
                                  <span className="submission-count pending">
                                    <i className="bi bi-clock-history"></i>
                                    {submittedCount}
                                  </span>
                                )}
                                {approvedCount > 0 && (
                                  <span className="submission-count completed">
                                    <i className="bi bi-check-circle-fill"></i>
                                    {approvedCount}
                                  </span>
                                )}
                                {rejectedCount > 0 && (
                                  <span className="submission-count rejected">
                                    <i className="bi bi-x-circle-fill"></i>
                                    {rejectedCount}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Overlay for when detail panel is open */}
          {showTaskDetail && (
            <div
              className="task-detail-overlay"
              onClick={() => setShowTaskDetail(false)}
            />
          )}

          {/* Task Detail Panel - Slide out from right */}
          <div className={`task-detail-panel ${showTaskDetail ? 'panel-open' : ''}`}>
            {selectedTask ? (
              <div className="task-detail-content">
                <div className="task-detail-header">
                  <div className="task-detail-header-main">
                    <div className="task-detail-title-area">
                      <h1 className="task-detail-title">{selectedTask.title}</h1>
                      <div className="task-detail-metadata">
                        <span className="task-detail-category-badge">
                          <i className="bi bi-tag-fill"></i>
                          {translateCategory(selectedTask.category)}
                        </span>
                        <span className="task-detail-points-badge">
                          <i className="bi bi-star-fill"></i>
                          {selectedTask.points} pts
                        </span>
                        <span className="task-detail-participants-badge">
                          <i className="bi bi-people-fill"></i>
                          {selectedTask.current_participants || 0}
                          {selectedTask.max_participants ? `/${selectedTask.max_participants}` : ''}
                        </span>
                        <span className="task-detail-date-badge">
                          <i className="bi bi-calendar3"></i>
                          {new Date(selectedTask.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="task-detail-header-actions">
                      <button
                        className="task-detail-action-btn new-tab"
                        onClick={() => {
                          const taskUrl = `/tasks/${selectedTask.id}`;
                          window.open(taskUrl, '_blank');
                        }}
                        title="Open in new tab"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>

                      <button
                        className="close-detail-btn"
                        onClick={() => setShowTaskDetail(false)}
                        title="Close"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <TaskDetails
                  selectedTask={selectedTask}
                  user={user}
                  userSubmission={userSubmission}
                  submissions={submissions}
                  submissionsLoading={submissionsLoading}
                  screenshotDisplayUrl={screenshotDisplayUrl}
                  submissionScreenshotUrls={submissionScreenshotUrls}
                  verificationSubmitted={verificationSubmitted}
                  onVerificationSubmitted={async () => {
                    if (processingReview) return; // Prevent multiple submissions

                    setProcessingReview(true);
                    setVerificationSubmitted(true);

                    try {
                      // Refresh data to update task counts and status
                      await refetchTaskData();

                      // Close the detail panel and clear selection after successful review
                      setShowTaskDetail(false);
                      setSelectedTask(null);
                    } catch (error) {
                      console.error('Error refreshing data after review:', error);
                    } finally {
                      setProcessingReview(false);
                    }
                  }}
                  onTaskClose={() => {
                    setShowTaskDetail(false);
                    setSelectedTask(null);
                  }}
                  onBackToTaskList={() => {
                    setShowTaskDetail(false);
                    setSelectedTask(null);
                  }}
                  showMobileView={showMobileView}
                  sidebarCollapsed={false}
                  onUpdateSubmission={async () => {
                    if (processingReview) return; // Prevent multiple updates

                    setProcessingReview(true);

                    try {
                      // Refresh data when submissions are updated
                      await refetchTaskData();
                    } catch (error) {
                      console.error('Error refreshing data after update:', error);
                    } finally {
                      setProcessingReview(false);
                    }
                  }}
                />
              </div>
            ) : null}
          </div>

        </div>
      </div>
    </BasePageLayout>
  );
};

export default PublisherPage;
